/**
 * Signup Page (WS8-C)
 *
 * Self-service signup: creates a user + organization in one step. Gated by
 * VITE_SIGNUP_ENABLED — when unset the page shows the contact screen, same
 * dark-until-configured pattern as Google Sign-In (the backend endpoint 404s
 * when SIGNUP_ENABLED is off, so the two flags are flipped together at
 * launch).
 *
 * Anti-abuse on the form: Cloudflare Turnstile (widget renders only when
 * VITE_TURNSTILE_SITE_KEY is set), a live org-URL availability probe, an
 * explicit Terms/Privacy checkbox, and a password checklist mirroring
 * backend passwordSchema (server stays the authority).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../context/AuthContext';
import { signup, signupWithGoogle, checkSlug, resendVerification } from '../../api/auth';
import { getErrorMessage } from '../../api/client';
import TurnstileWidget from '../../components/auth/TurnstileWidget';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import Logo from '../../components/brand/Logo';

/** Display-only decode of the ID token's email (the server re-verifies). */
function decodeGoogleEmail(idToken: string): string | null {
  try {
    const payload = JSON.parse(
      atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { email?: string };
    return payload.email ?? null;
  } catch {
    return null;
  }
}

// =============================================================================
// Password rules — mirror backend/src/schemas/auth.schema.ts passwordSchema
// (same live-checklist pattern as /platform/account)
// =============================================================================

const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw) => /[0-9]/.test(pw) },
];

// =============================================================================
// Slug helpers
// =============================================================================

/** Org name → URL slug matching the backend's format rules. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[^a-z]+/, '') // must start with a letter
    .replace(/-+$/, '')
    .slice(0, 50);
}

const SLUG_FORMAT = /^[a-z][a-z0-9-]*[a-z0-9]$/;

// =============================================================================
// Validation schema
// =============================================================================

const signupFormSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(100)
      .regex(/[A-Z]/, 'Password must contain an uppercase letter')
      .regex(/[a-z]/, 'Password must contain a lowercase letter')
      .regex(/[0-9]/, 'Password must contain a number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    organizationName: z
      .string()
      .min(2, 'Organization name must be at least 2 characters')
      .max(100),
    organizationSlug: z
      .string()
      .min(3, 'URL must be at least 3 characters')
      .max(50)
      .regex(
        SLUG_FORMAT,
        'Lowercase letters, numbers, and hyphens; must start with a letter'
      )
      .refine((s) => !s.includes('--'), 'No consecutive hyphens'),
    acceptTerms: z.boolean().refine((v) => v === true, {
      message: 'You must accept the Terms of Service and Privacy Policy',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof signupFormSchema>;

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid';

// =============================================================================
// Contact screen (shown while signup is disabled)
// =============================================================================

function ContactScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
        <div className="w-16 h-16 bg-primary-100 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Interested in ProgramRow?</h1>
        <p className="text-gray-600 mb-6">
          Self-service signup is not available at this time. To learn more or request access, please reach out.
        </p>
        <a
          href="mailto:info@programrow.com"
          className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          Contact info@programrow.com
        </a>
        <p className="mt-6 text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Post-signup verification screen
// =============================================================================

function CheckEmailScreen({ email }: { email: string }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification({ email });
      setResent(true);
    } catch {
      // The endpoint intentionally succeeds quietly; a failure here is
      // transient — leave the button usable.
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
        <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Check your email</h1>
        <p className="text-gray-600 mb-2">
          We sent a verification link to <span className="font-medium text-gray-900">{email}</span>.
        </p>
        <p className="text-gray-600 mb-6">
          Click the link to activate your account and get started.
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || resent}
          className="text-sm font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400"
        >
          {resent ? 'Verification email resent' : resending ? 'Resending…' : "Didn't get it? Resend the email"}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Signup form
// =============================================================================

function SignupForm() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<null | string>(null);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugEdited, setSlugEdited] = useState(false);
  const slugCheckSeq = useRef(0);
  // Google-based signup: identity comes from the credential; the password
  // and name fields disappear and the server verifies the token.
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const googleEmail = googleCredential ? decodeGoogleEmail(googleCredential) : null;

  const turnstileConfigured = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: { acceptTerms: false },
  });

  const password = watch('password') ?? '';
  const organizationName = watch('organizationName') ?? '';
  const organizationSlug = watch('organizationSlug') ?? '';

  // Auto-slugify from the org name until the user edits the slug by hand
  useEffect(() => {
    if (slugEdited) return;
    const next = slugify(organizationName);
    setValue('organizationSlug', next, { shouldValidate: organizationName.length > 0 });
  }, [organizationName, slugEdited, setValue]);

  // Debounced live availability probe
  useEffect(() => {
    const slug = organizationSlug;
    if (!slug || slug.length < 3 || !SLUG_FORMAT.test(slug) || slug.includes('--')) {
      setSlugStatus(slug ? 'invalid' : 'idle');
      return;
    }
    setSlugStatus('checking');
    const seq = ++slugCheckSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await checkSlug(slug);
        if (slugCheckSeq.current !== seq) return; // superseded by newer input
        if (result.available) setSlugStatus('available');
        else if (result.reason === 'reserved') setSlugStatus('reserved');
        else if (result.reason === 'taken') setSlugStatus('taken');
        else setSlugStatus('invalid');
      } catch {
        if (slugCheckSeq.current === seq) setSlugStatus('idle'); // probe failed — server will decide at submit
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [organizationSlug]);

  const slugMessage = useMemo(() => {
    switch (slugStatus) {
      case 'checking':
        return { text: 'Checking availability…', className: 'text-gray-500' };
      case 'available':
        return { text: '✓ Available', className: 'text-green-600' };
      case 'taken':
        return { text: 'Already in use. Choose a different URL', className: 'text-red-600' };
      case 'reserved':
        return { text: 'This URL is not available', className: 'text-red-600' };
      default:
        return null;
    }
  }, [slugStatus]);

  const onSubmit = async (data: SignupFormData) => {
    setError(null);
    if (turnstileConfigured && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await signup({
        ...data,
        organizationSlug: data.organizationSlug.toLowerCase(),
        turnstileToken: turnstileToken ?? undefined,
      });
      if (response.requiresVerification) {
        setVerificationEmail(data.email);
      } else {
        await refreshUser();
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google path: validate only the org + terms fields (identity rides the
  // token), then create-and-log-in — no verification email round trip.
  const onGoogleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleCredential) return;
    setError(null);
    const orgFieldsValid = await trigger(['organizationName', 'organizationSlug', 'acceptTerms']);
    if (!orgFieldsValid) return;
    if (turnstileConfigured && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }
    setIsSubmitting(true);
    try {
      const values = getValues();
      await signupWithGoogle({
        idToken: googleCredential,
        organizationName: values.organizationName,
        organizationSlug: values.organizationSlug.toLowerCase(),
        acceptTerms: true,
        turnstileToken: turnstileToken ?? undefined,
      });
      await refreshUser();
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (verificationEmail) {
    return <CheckEmailScreen email={verificationEmail} />;
  }

  const inputClass = (hasError: boolean) => `
    block w-full px-3 py-2 border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack
    ${hasError ? 'border-red-300' : 'border-gray-300'}
  `;

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <Link to="/" aria-label="ProgramRow home" className="inline-flex">
            <Logo size={22} on="light" wordmarkSize={30} />
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Create your organization</h1>
          <p className="mt-2 text-sm text-gray-600">
            Free to start. No credit card required.
          </p>
        </div>

        <div className="bg-white p-8 border border-hairline">
          <form
            onSubmit={googleCredential ? onGoogleSubmit : handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Google identity banner (replaces name/email/password fields) */}
            {googleCredential && (
              <div className="flex items-start justify-between gap-3 p-3 bg-surface-panel border border-hairline border-l-[3px] border-l-rack">
                <p className="text-sm text-primary-900">
                  Signing up with Google as{' '}
                  <span className="font-medium">{googleEmail ?? 'your Google account'}</span>.
                  No password needed.
                </p>
                <button
                  type="button"
                  onClick={() => setGoogleCredential(null)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  Use email instead
                </button>
              </div>
            )}

            {/* Name + email — identity comes from Google when a credential is set */}
            {!googleCredential && (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  autoFocus
                  {...register('firstName')}
                  className={inputClass(!!errors.firstName)}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  {...register('lastName')}
                  className={inputClass(!!errors.lastName)}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Work email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className={inputClass(!!errors.email)}
                placeholder="you@yourschool.edu"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
            </>
            )}

            {/* Organization */}
            <div>
              <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                Organization name
              </label>
              <input
                id="organizationName"
                type="text"
                autoComplete="organization"
                {...register('organizationName')}
                className={inputClass(!!errors.organizationName)}
                placeholder="Pittsburgh Sports Network"
              />
              {errors.organizationName && (
                <p className="mt-1 text-sm text-red-600">{errors.organizationName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="organizationSlug" className="block text-sm font-medium text-gray-700 mb-1">
                Organization URL
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  programrow.com/
                </span>
                <input
                  id="organizationSlug"
                  type="text"
                  {...register('organizationSlug', {
                    onChange: () => setSlugEdited(true),
                  })}
                  className={`
                    block w-full px-3 py-2 border rounded-r-md shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack
                    ${errors.organizationSlug || slugStatus === 'taken' || slugStatus === 'reserved' ? 'border-red-300' : 'border-gray-300'}
                  `}
                  placeholder="pittsburgh-sports"
                />
              </div>
              {errors.organizationSlug ? (
                <p className="mt-1 text-sm text-red-600">{errors.organizationSlug.message}</p>
              ) : (
                slugMessage && <p className={`mt-1 text-sm ${slugMessage.className}`}>{slugMessage.text}</p>
              )}
            </div>

            {/* Password — not needed when Google carries the identity */}
            {!googleCredential && (
            <>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
                className={inputClass(!!errors.password)}
              />
              <ul className="mt-2 space-y-1">
                {PASSWORD_RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <li
                      key={rule.label}
                      className={`text-xs flex items-center gap-1.5 ${met ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      <span aria-hidden="true">{met ? '✓' : '○'}</span>
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
                className={inputClass(!!errors.confirmPassword)}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
            </>
            )}

            {/* Terms acceptance — a checkbox, not fine print (WS8-C) */}
            <div>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  {...register('acceptTerms')}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-rack"
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary-600 hover:text-primary-500 font-medium">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" target="_blank" className="text-primary-600 hover:text-primary-500 font-medium">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="mt-1 text-sm text-red-600">{errors.acceptTerms.message}</p>
              )}
            </div>

            <TurnstileWidget onToken={setTurnstileToken} />

            <button
              type="submit"
              disabled={isSubmitting || slugStatus === 'taken' || slugStatus === 'reserved'}
              className="w-full flex justify-center px-4 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting
                ? 'Creating your organization…'
                : googleCredential
                  ? 'Create organization with Google'
                  : 'Create organization'}
            </button>

            {/* Google alternative: no password to invent, no verification
                email to wait for. Renders nothing when GIS is unconfigured. */}
            {!googleCredential && (
              <GoogleSignInButton text="continue_with" onCredential={(idToken) => setGoogleCredential(idToken)} />
            )}
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Page
// =============================================================================

export default function Signup() {
  const signupEnabled = import.meta.env.VITE_SIGNUP_ENABLED === 'true';
  return signupEnabled ? <SignupForm /> : <ContactScreen />;
}
